import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Bell,
  Brain,
  CheckCircle,
  ClipboardCheck,
  Copy,
  Download,
  FileText,
  Image as ImageIcon,
  KeyRound,
  Loader2,
  Mail,
  PackageCheck,
  Plus,
  RadioTower,
  RefreshCw,
  ShieldCheck,
  Smartphone,
  Trash2,
  Upload,
  UserCheck,
  UserX,
  Users,
  WifiOff,
  Wrench,
  X
} from 'lucide-react';
import { useAppContext } from '../../contexts/AppContext';
import { MaintenanceEntry, Part, Truck, User } from '../../types';
import {
  ComplianceDocumentType,
  FieldRole,
  fleetOpsService,
  FleetOpsWorkspace,
  IntegrationProvider,
  Severity,
  TeamMember,
  WorkOrder,
  WorkOrderPartReservation
} from '../../services/FleetOpsService';
import { notificationService, WebPushStatus } from '../../services/NotificationService';
import teamWorkspaceService from '../../services/TeamWorkspaceService';
import { downloadTextFile } from '../../utils/dataExchange';
import { formatCostPerDistance, formatDistance, fromDisplayDistance, getDistanceUnitLabel, toDisplayDistance, useDistanceUnit } from '../../utils/distance';

export type FleetOpsSection = 'field-work' | 'work-orders' | 'integrations' | 'compliance' | 'predictive' | 'company' | 'reports';

interface FleetOpsSuitePageProps {
  section: FleetOpsSection;
}

interface InspectionFormState {
  truckId: string;
  inspectorName: string;
  inspectorRole: FieldRole;
  odometer: string;
  notes: string;
  signatureName: string;
  photoDataUrl?: string;
  offlineMode: boolean;
  checklist: Record<string, boolean>;
}

interface IssueFormState {
  truckId: string;
  reportedBy: string;
  severity: Severity;
  category: 'Safety' | 'Mechanical' | 'Tires' | 'Brakes' | 'Electrical' | 'Body' | 'Other';
  description: string;
  photoDataUrl?: string;
}

interface WorkOrderFormState {
  truckId: string;
  title: string;
  type: WorkOrder['type'];
  priority: Severity;
  assignedTo: string;
  scheduledDate: string;
  laborHours: string;
  laborRate: string;
  partId: string;
  partQuantity: string;
  approvalRequired: boolean;
  notes: string;
}

interface ComplianceFormState {
  truckId: string;
  type: ComplianceDocumentType;
  documentNumber: string;
  issuer: string;
  expirationDate: string;
  owner: string;
  attachmentName?: string;
  attachmentDataUrl?: string;
}

interface MemberFormState {
  name: string;
  email: string;
  role: TeamMember['role'];
  permissions: string;
}

interface OperationMessage {
  tone: 'success' | 'warning' | 'danger' | 'info';
  title: string;
  detail: string;
}

type DetailRecord =
  | { type: 'inspection'; record: import('../../services/FleetOpsService').FleetInspection }
  | { type: 'issue'; record: import('../../services/FleetOpsService').IssueReport }
  | { type: 'compliance'; record: import('../../services/FleetOpsService').ComplianceDocument };

const sectionConfig: Record<FleetOpsSection, { title: string; subtitle: string; icon: React.ReactNode }> = {
  'field-work': {
    title: 'Driver & Mechanic Mobile Workflows',
    subtitle: 'Inspections, DVIRs, photos, signatures, issue reports, and offline queue.',
    icon: <Smartphone size={24} />
  },
  'work-orders': {
    title: 'Work Orders',
    subtitle: 'Assignments, labor, parts reservation, approvals, invoices, and completion flow.',
    icon: <Wrench size={24} />
  },
  integrations: {
    title: 'Telematics & Push Integrations',
    subtitle: 'Web Push, Samsara, Motive, Geotab, GPS/ELD imports, faults, fuel, and odometer feeds.',
    icon: <RadioTower size={24} />
  },
  compliance: {
    title: 'Compliance Center',
    subtitle: 'DVIR history, DOT records, renewals, insurance, registration, and audit activity.',
    icon: <ShieldCheck size={24} />
  },
  predictive: {
    title: 'Predictive Maintenance',
    subtitle: 'Risk scores from mileage, service history, open issues, work orders, and telematics faults.',
    icon: <Brain size={24} />
  },
  company: {
    title: 'Company Accounts',
    subtitle: 'Team members, roles, permissions, account status, and audit history.',
    icon: <Users size={24} />
  },
  reports: {
    title: 'Fleet Reporting',
    subtitle: 'Cost by distance, downtime, PM compliance, fuel trends, utilization, and CSV export.',
    icon: <BarChart3 size={24} />
  }
};

const sectionLinks: Array<{ section: FleetOpsSection; label: string; path: string }> = [
  { section: 'field-work', label: 'Field Work', path: '/field-work' },
  { section: 'work-orders', label: 'Work Orders', path: '/work-orders' },
  { section: 'integrations', label: 'Integrations', path: '/integrations' },
  { section: 'compliance', label: 'Compliance', path: '/compliance' },
  { section: 'predictive', label: 'Predictive', path: '/predictive-maintenance' },
  { section: 'reports', label: 'Reports', path: '/reports' },
  { section: 'company', label: 'Company', path: '/company' }
];

const checklistItems = [
  { key: 'brakes', label: 'Brakes' },
  { key: 'tires', label: 'Tires' },
  { key: 'lights', label: 'Lights' },
  { key: 'fluids', label: 'Fluids' },
  { key: 'safetyKit', label: 'Safety kit' }
];

const formatCurrency = (value: number): string => value.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
const formatDate = (date?: Date): string => date ? new Date(date).toLocaleDateString() : 'Not set';
const statusClass = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]+/g, '-');
const getFleetWorkspaceId = (user: User | null | undefined): string => {
  if (!user) return 'local-user';
  return user.companyId && user.companyId !== 'default-company' ? user.companyId : user.id;
};

const permissionPresets: Record<TeamMember['role'], string> = {
  admin: 'manage-company,manage-users,work-orders,compliance,reports,integrations,inspections,issues',
  editor: 'work-orders,compliance,reports,inspections,issues,parts',
  viewer: 'reports,compliance,read-only',
  driver: 'inspections,issues,offline-mode',
  mechanic: 'work-orders,inspections,issues,parts',
  dispatcher: 'work-orders,reports,issues'
};

const isValidEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
const isPendingMember = (status: TeamMember['status']): boolean => status === 'pending' || status === 'invited';
const getTeamStatusLabel = (status: TeamMember['status']): string => isPendingMember(status) ? 'pending' : status;
const getTeamStatusClass = (status: TeamMember['status']): string => isPendingMember(status) ? 'pending' : statusClass(status);
const PRODUCTION_INVITE_API_URL = 'https://fleet-fix-alpha.vercel.app/api/team-invite';
const getTeamInviteApiUrl = (): string => {
  const configuredUrl = process.env.REACT_APP_TEAM_INVITE_API_URL?.trim();
  if (configuredUrl) return configuredUrl;

  if (typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname)) {
    return PRODUCTION_INVITE_API_URL;
  }

  return '/api/team-invite';
};

const FleetOpsSuitePage: React.FC<FleetOpsSuitePageProps> = ({ section }) => {
  const { state } = useAppContext();
  const trucks = state.trucks as Truck[];
  const maintenance = state.maintenance as MaintenanceEntry[];
  const parts = state.parts as Part[];
  const distanceUnit = useDistanceUnit();
  const distanceUnitLabel = getDistanceUnitLabel(distanceUnit);
  const currentUser = state.currentUser;
  const userId = getFleetWorkspaceId(currentUser);
  const actor = currentUser?.name || 'Fleet Manager';
  const companyName = currentUser?.companyId && currentUser.companyId !== currentUser.id ? 'FleetFix Team Workspace' : `${actor}'s Fleet`;

  const [workspace, setWorkspace] = useState<FleetOpsWorkspace>(() => fleetOpsService.getWorkspace(userId));
  const [webPushStatus, setWebPushStatus] = useState<WebPushStatus>(() => notificationService.getWebPushStatus());
  const [pushVapidKey, setPushVapidKey] = useState(() => notificationService.getWebPushSettings().vapidPublicKey);
  const [pushEndpoint, setPushEndpoint] = useState(() => notificationService.getWebPushSettings().subscriptionEndpoint);
  const [csvImport, setCsvImport] = useState('truckId,recordedAt,miles,fuelGallons,faultCode,severity,description\n');
  const [operationMessage, setOperationMessage] = useState<OperationMessage | null>(null);
  const [sendingInviteTarget, setSendingInviteTarget] = useState<string | null>(null);
  const [detailRecord, setDetailRecord] = useState<DetailRecord | null>(null);

  const firstTruckId = trucks[0]?.id || '';
  const firstPartId = parts[0]?.id || '';

  const [inspectionForm, setInspectionForm] = useState<InspectionFormState>({
    truckId: firstTruckId,
    inspectorName: actor,
    inspectorRole: 'Driver',
    odometer: trucks[0]?.mileage !== undefined ? Math.round(toDisplayDistance(trucks[0].mileage, distanceUnit)).toString() : '',
    notes: '',
    signatureName: actor,
    offlineMode: false,
    checklist: checklistItems.reduce<Record<string, boolean>>((acc, item) => {
      acc[item.key] = true;
      return acc;
    }, {})
  });

  const [issueForm, setIssueForm] = useState<IssueFormState>({
    truckId: firstTruckId,
    reportedBy: actor,
    severity: 'medium',
    category: 'Mechanical',
    description: ''
  });

  const [workOrderForm, setWorkOrderForm] = useState<WorkOrderFormState>({
    truckId: firstTruckId,
    title: '',
    type: 'repair',
    priority: 'medium',
    assignedTo: 'Internal shop',
    scheduledDate: new Date().toISOString().slice(0, 10),
    laborHours: '2',
    laborRate: '95',
    partId: firstPartId,
    partQuantity: '1',
    approvalRequired: true,
    notes: ''
  });

  const [complianceForm, setComplianceForm] = useState<ComplianceFormState>({
    truckId: firstTruckId,
    type: 'DOT Inspection',
    documentNumber: '',
    issuer: '',
    expirationDate: new Date(Date.now() + 365 * 86400000).toISOString().slice(0, 10),
    owner: actor
  });

  const [memberForm, setMemberForm] = useState<MemberFormState>({
    name: '',
    email: '',
    role: 'driver',
    permissions: 'inspections,issues'
  });

  useEffect(() => {
    setWorkspace(fleetOpsService.getWorkspace(userId));
  }, [userId]);

  useEffect(() => {
    if (!currentUser) return;

    const timeout = window.setTimeout(() => {
      void teamWorkspaceService.publishSnapshot(currentUser, {
        trucks,
        maintenance,
        parts
      });
    }, 600);

    return () => window.clearTimeout(timeout);
  }, [currentUser, workspace, trucks, maintenance, parts]);

  useEffect(() => {
    if (section !== 'company') return;
    let cancelled = false;
    void fleetOpsService.syncRemoteTeamInviteStatuses(userId).then(updated => {
      if (!cancelled) setWorkspace(updated);
    });
    return () => {
      cancelled = true;
    };
  }, [section, userId]);

  useEffect(() => {
    setInspectionForm(form => ({ ...form, truckId: form.truckId || firstTruckId, inspectorName: form.inspectorName || actor, signatureName: form.signatureName || actor }));
    setIssueForm(form => ({ ...form, truckId: form.truckId || firstTruckId, reportedBy: form.reportedBy || actor }));
    setWorkOrderForm(form => ({ ...form, truckId: form.truckId || firstTruckId, partId: form.partId || firstPartId }));
    setComplianceForm(form => ({ ...form, truckId: form.truckId || firstTruckId, owner: form.owner || actor }));
  }, [actor, firstPartId, firstTruckId]);

  const predictiveScores = useMemo(
    () => fleetOpsService.calculatePredictiveScores(trucks, maintenance, workspace),
    [trucks, maintenance, workspace]
  );

  const reportMetrics = useMemo(
    () => fleetOpsService.calculateReports(trucks, maintenance, workspace),
    [trucks, maintenance, workspace]
  );

  const getTruck = (truckId: string): Truck | undefined => trucks.find(truck => truck.id === truckId);
  const getTruckLabel = (truckId: string): string => {
    const truck = getTruck(truckId);
    return truck ? `${truck.nickname || truck.licensePlate} · ${truck.year} ${truck.make} ${truck.model}` : 'Unassigned vehicle';
  };

  const readFileAsDataUrl = (file: File, callback: (value: string) => void): void => {
    const reader = new FileReader();
    reader.onload = () => callback(String(reader.result || ''));
    reader.readAsDataURL(file);
  };

  const selectedPartReservation = (): WorkOrderPartReservation[] => {
    const part = parts.find(item => item.id === workOrderForm.partId);
    const quantity = Number(workOrderForm.partQuantity) || 0;
    if (!part || quantity <= 0) return [];

    return [{
      partId: part.id,
      partName: part.name,
      quantity,
      unitCost: part.cost || 0
    }];
  };

  const showMessage = (message: OperationMessage): void => {
    setOperationMessage(message);
  };

  const commitWorkspace = (updated: FleetOpsWorkspace): void => {
    setWorkspace(updated);
    if (!currentUser) return;
    void teamWorkspaceService.publishSnapshot(currentUser, {
      trucks,
      maintenance,
      parts
    });
  };

  const copyText = async (value: string, label: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      showMessage({ tone: 'success', title: `${label} copied`, detail: 'Ready to paste into your email, chat, or onboarding note.' });
    } catch (error) {
      showMessage({ tone: 'warning', title: 'Copy blocked', detail: value });
    }
  };

  const sendTeamInviteEmail = async (member: TeamMember): Promise<boolean> => {
    setSendingInviteTarget(member.id);
    showMessage({
      tone: 'info',
      title: 'Sending invite email',
      detail: `FleetFix is emailing ${member.email} through the invite service.`
    });

    try {
      const response = await fetch(getTeamInviteApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: member.email,
          inviteeName: member.name,
          inviterName: actor,
          companyName,
          role: member.role,
          inviteCode: member.inviteCode,
          inviteLink: member.inviteLink,
          expiresAt: formatDate(member.inviteExpiresAt)
        })
      });

      if (response.ok) {
        showMessage({ tone: 'success', title: 'Invite email sent', detail: `${member.email} received the FleetFix workspace invite.` });
        return true;
      }

      const result = await response.json().catch(() => null);
      showMessage({
        tone: response.status === 503 ? 'warning' : 'danger',
        title: response.status === 503 ? 'Email provider setup needed' : 'Invite email failed',
        detail: result?.setup || result?.error || 'The member was saved, but the email service rejected the invite.'
      });
    } catch (error) {
      showMessage({
        tone: 'danger',
        title: 'Invite email failed',
        detail: 'FleetFix could not reach the invite API. The member was saved, and the Copy Link button is available as a manual backup.'
      });
    } finally {
      setSendingInviteTarget(current => current === member.id ? null : current);
    }

    return false;
  };

  const handleInspectionSubmit = (event: FormEvent) => {
    event.preventDefault();
    const failedItems = Object.values(inspectionForm.checklist).filter(value => !value).length;
    const status = failedItems >= 2 ? 'repair-required' : failedItems === 1 ? 'attention-needed' : 'passed';
    const synced = !inspectionForm.offlineMode && navigator.onLine;

    const updated = fleetOpsService.createInspection(userId, {
      truckId: inspectionForm.truckId,
      truckLabel: getTruckLabel(inspectionForm.truckId),
      inspectorName: inspectionForm.inspectorName,
      inspectorRole: inspectionForm.inspectorRole,
      odometer: inspectionForm.odometer
        ? Math.round(fromDisplayDistance(Number(inspectionForm.odometer), distanceUnit))
        : getTruck(inspectionForm.truckId)?.mileage || 0,
      checklist: inspectionForm.checklist,
      notes: inspectionForm.notes,
      signatureName: inspectionForm.signatureName,
      photoDataUrl: inspectionForm.photoDataUrl,
      issueCount: failedItems,
      status,
      synced
    }, actor);

    if (status !== 'passed') {
      notificationService.addNotification({
        type: 'alert',
        title: 'Inspection Needs Attention',
        message: `${getTruckLabel(inspectionForm.truckId)} has ${failedItems} failed inspection item${failedItems > 1 ? 's' : ''}.`,
        priority: status === 'repair-required' ? 'urgent' : 'high',
        actionRequired: true,
        relatedEntity: { type: 'truck', id: inspectionForm.truckId, name: getTruckLabel(inspectionForm.truckId) }
      });
    }

    commitWorkspace(updated);
    setInspectionForm(form => ({ ...form, notes: '', photoDataUrl: undefined }));
    showMessage({
      tone: status === 'passed' ? 'success' : 'warning',
      title: status === 'passed' ? 'Inspection saved' : 'Inspection flagged',
      detail: status === 'passed'
        ? 'DVIR inspection was saved to the fleet record.'
        : `${failedItems} failed checklist item${failedItems > 1 ? 's' : ''} recorded for follow-up.`
    });
  };

  const handleIssueSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!issueForm.description.trim()) return;

    const updated = fleetOpsService.createIssueReport(userId, {
      ...issueForm,
      truckLabel: getTruckLabel(issueForm.truckId)
    }, actor);
    commitWorkspace(updated);
    setIssueForm(form => ({ ...form, description: '', photoDataUrl: undefined }));
    showMessage({
      tone: issueForm.severity === 'critical' ? 'danger' : 'success',
      title: 'Issue logged',
      detail: `${issueForm.category} issue captured for ${getTruckLabel(issueForm.truckId)}.`
    });
  };

  const handleWorkOrderSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!workOrderForm.title.trim()) return;
    const selectedPart = parts.find(part => part.id === workOrderForm.partId);
    const requestedQuantity = Number(workOrderForm.partQuantity) || 0;
    if (selectedPart?.inventoryLevel !== undefined && requestedQuantity > selectedPart.inventoryLevel) {
      showMessage({
        tone: 'danger',
        title: 'Reservation blocked',
        detail: `${selectedPart.name} has ${selectedPart.inventoryLevel} available, but ${requestedQuantity} were requested.`
      });
      return;
    }

    const updated = fleetOpsService.createWorkOrder(userId, {
      truckId: workOrderForm.truckId,
      truckLabel: getTruckLabel(workOrderForm.truckId),
      title: workOrderForm.title,
      type: workOrderForm.type,
      priority: workOrderForm.priority,
      status: workOrderForm.approvalRequired ? 'awaiting-approval' : 'approved',
      assignedTo: workOrderForm.assignedTo,
      requestedBy: actor,
      scheduledDate: new Date(`${workOrderForm.scheduledDate}T09:00:00`),
      laborHours: Number(workOrderForm.laborHours) || 0,
      laborRate: Number(workOrderForm.laborRate) || 0,
      parts: selectedPartReservation(),
      approvalRequired: workOrderForm.approvalRequired,
      invoiceStatus: 'not-created',
      notes: workOrderForm.notes
    }, actor);

    commitWorkspace(updated);
    setWorkOrderForm(form => ({ ...form, title: '', notes: '' }));
    showMessage({
      tone: 'success',
      title: 'Work order created',
      detail: `${workOrderForm.title} is ready in the shop workflow.`
    });
  };

  const handleComplianceSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!complianceForm.documentNumber.trim()) return;

    const updated = fleetOpsService.createComplianceDocument(userId, {
      truckId: complianceForm.truckId,
      truckLabel: getTruckLabel(complianceForm.truckId),
      type: complianceForm.type,
      documentNumber: complianceForm.documentNumber,
      issuer: complianceForm.issuer,
      expirationDate: new Date(`${complianceForm.expirationDate}T12:00:00`),
      owner: complianceForm.owner,
      attachmentName: complianceForm.attachmentName,
      attachmentDataUrl: complianceForm.attachmentDataUrl
    }, actor);

    commitWorkspace(updated);
    setComplianceForm(form => ({ ...form, documentNumber: '', issuer: '', attachmentName: undefined, attachmentDataUrl: undefined }));
    showMessage({
      tone: 'success',
      title: 'Compliance record saved',
      detail: `${complianceForm.type} renewal is now tracked for ${getTruckLabel(complianceForm.truckId)}.`
    });
  };

  const handleMemberSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!memberForm.email.trim() || !memberForm.name.trim()) return;

    const normalizedEmail = memberForm.email.trim().toLowerCase();
    if (!isValidEmail(normalizedEmail)) {
      showMessage({ tone: 'danger', title: 'Invalid email', detail: 'Enter a valid email address before sending the invite.' });
      return;
    }
    const updated = fleetOpsService.addTeamMember(userId, {
      name: memberForm.name,
      email: normalizedEmail,
      role: memberForm.role,
      permissions: memberForm.permissions.split(',').map(permission => permission.trim()).filter(Boolean)
    }, actor);

    commitWorkspace(updated);
    const invitedMember = updated.teamMembers.find(member => member.email === normalizedEmail);
    setMemberForm(form => ({ ...form, name: '', email: '' }));

    if (invitedMember) {
      void fleetOpsService.publishTeamInvite(userId, invitedMember, companyName);
      await sendTeamInviteEmail(invitedMember);
    } else {
      showMessage({
        tone: 'warning',
        title: 'Invite already exists',
        detail: `${normalizedEmail} already has a workspace record. Use Resend on the team card.`
      });
    }
  };

  const resendInvite = async (member: TeamMember): Promise<void> => {
    const updated = fleetOpsService.resendTeamInvite(userId, member.id, actor);
    commitWorkspace(updated);
    const refreshed = updated.teamMembers.find(item => item.id === member.id);
    if (refreshed) {
      void fleetOpsService.publishTeamInvite(userId, refreshed, companyName);
      await sendTeamInviteEmail(refreshed);
    }
  };

  const revokeAccess = (member: TeamMember): void => {
    commitWorkspace(fleetOpsService.revokeTeamInvite(userId, member.id, actor));
    showMessage({ tone: 'warning', title: 'Access revoked', detail: `${member.email} can no longer use this invite or workspace access.` });
  };

  const updateWorkOrderStatus = (workOrder: WorkOrder, status: WorkOrder['status']) => {
    const updates: Partial<WorkOrder> = { status };
    if (status === 'approved') {
      updates.approvedBy = actor;
      updates.approvedAt = new Date();
    }
    if (status === 'invoiced') {
      updates.invoiceStatus = 'sent';
      updates.invoiceNumber = workOrder.invoiceNumber || `INV-${new Date().getFullYear()}-${workOrder.id.slice(-5).toUpperCase()}`;
    }

    commitWorkspace(fleetOpsService.updateWorkOrder(userId, workOrder.id, updates, actor));
    showMessage({
      tone: 'success',
      title: 'Work order updated',
      detail: `${workOrder.title} moved to ${status}.`
    });
  };

  const runConnectorSync = (connectorId: IntegrationProvider) => {
    commitWorkspace(fleetOpsService.runTelematicsSync(userId, connectorId, trucks, actor));
    notificationService.addNotification({
      type: 'info',
      title: `${connectorId} Sync Complete`,
      message: 'Telematics records were imported into FleetFix.',
      priority: 'medium',
      actionRequired: false
    });
    showMessage({
      tone: 'success',
      title: 'Sync complete',
      detail: `${connectorId} records are now available for reporting and risk scoring.`
    });
  };

  const updateIssueStatus = (issueId: string, status: 'triaged' | 'resolved') => {
    const updated = fleetOpsService.updateIssueReport(userId, issueId, { status }, actor);
    commitWorkspace(updated);
    showMessage({
      tone: status === 'resolved' ? 'success' : 'info',
      title: status === 'resolved' ? 'Issue resolved' : 'Issue triaged',
      detail: 'The field report status was updated and added to the audit trail.'
    });
  };

  const convertIssueToWorkOrder = (issueId: string) => {
    const updated = fleetOpsService.convertIssueToWorkOrder(userId, issueId, actor);
    commitWorkspace(updated);
    showMessage({
      tone: 'success',
      title: 'Work order generated',
      detail: 'The issue is now connected to a shop workflow.'
    });
  };

  const deleteInspectionRecord = (inspection: import('../../services/FleetOpsService').FleetInspection) => {
    if (!window.confirm(`Delete the inspection record for ${inspection.truckLabel}? This cannot be undone.`)) return;

    const updated = fleetOpsService.deleteInspection(userId, inspection.id, actor);
    commitWorkspace(updated);
    setDetailRecord(current => current?.type === 'inspection' && current.record.id === inspection.id ? null : current);
    showMessage({
      tone: 'warning',
      title: 'Inspection deleted',
      detail: `${inspection.truckLabel} inspection was removed from Field Work.`
    });
  };

  const deleteIssueRecord = (issue: import('../../services/FleetOpsService').IssueReport) => {
    const convertedMessage = issue.status === 'converted-to-work-order'
      ? ' The connected work order will remain in Work Orders.'
      : '';
    if (!window.confirm(`Delete the ${issue.category.toLowerCase()} issue for ${issue.truckLabel}? This cannot be undone.${convertedMessage}`)) return;

    const updated = fleetOpsService.deleteIssueReport(userId, issue.id, actor);
    commitWorkspace(updated);
    setDetailRecord(current => current?.type === 'issue' && current.record.id === issue.id ? null : current);
    showMessage({
      tone: 'warning',
      title: 'Issue deleted',
      detail: `${issue.category} issue for ${issue.truckLabel} was removed from Field Work.`
    });
  };

  const handlePushSubscribe = async () => {
    try {
      const status = await notificationService.subscribeToWebPush({
        vapidPublicKey: pushVapidKey.trim() || undefined,
        subscriptionEndpoint: pushEndpoint.trim() || undefined
      });
      setWebPushStatus(status);
      showMessage({
        tone: status.subscribed ? 'success' : 'warning',
        title: status.subscribed ? 'Push enabled' : 'Push setup updated',
        detail: status.message
      });
    } catch (error) {
      setWebPushStatus({
        ...notificationService.getWebPushStatus(),
        message: error instanceof Error ? error.message : 'Unable to subscribe this browser for Web Push.'
      });
      showMessage({
        tone: 'danger',
        title: 'Push setup failed',
        detail: error instanceof Error ? error.message : 'Unable to subscribe this browser for Web Push.'
      });
    }
  };

  const handleCsvImport = () => {
    commitWorkspace(fleetOpsService.importTelematicsCsv(userId, csvImport, trucks, actor));
    const importedRows = Math.max(0, csvImport.split(/\r?\n/).filter(row => row.trim()).length - 1);
    showMessage({
      tone: importedRows > 0 ? 'success' : 'warning',
      title: importedRows > 0 ? 'CSV imported' : 'CSV needs records',
      detail: importedRows > 0
        ? `${importedRows} GPS/ELD row${importedRows === 1 ? '' : 's'} were processed.`
        : 'Paste provider export rows below the header before importing.'
    });
  };

  const downloadReport = () => {
    const csv = fleetOpsService.exportReportCsv(trucks, maintenance, workspace);
    downloadTextFile(
      `fleetfix-report-${new Date().toISOString().slice(0, 10)}.csv`,
      csv,
      'text/csv;charset=utf-8;'
    );
    showMessage({
      tone: 'success',
      title: 'Report exported',
      detail: 'Fleet performance CSV is ready for finance or operations review.'
    });
  };

  const renderTruckSelect = (value: string, onChange: (value: string) => void) => (
    <select value={value} onChange={event => onChange(event.target.value)} required>
      {trucks.map(truck => (
        <option key={truck.id} value={truck.id}>{getTruckLabel(truck.id)}</option>
      ))}
    </select>
  );

  const renderMetricCard = (label: string, value: string | number, icon: React.ReactNode, tone = 'neutral') => (
    <div className={`ops-metric-card ${tone}`}>
      <span className="ops-metric-icon">{icon}</span>
      <div>
        <strong>{value}</strong>
        <span>{label}</span>
      </div>
    </div>
  );

  const renderEmptyState = (title: string, message: string, icon: React.ReactNode) => (
    <div className="ops-empty-state">
      <span>{icon}</span>
      <strong>{title}</strong>
      <p>{message}</p>
    </div>
  );

  const renderPageHeader = () => {
    const config = sectionConfig[section];
    return (
      <div className="page-header ops-page-header">
        <div className="ops-title-block">
          <span className="ops-title-icon">{config.icon}</span>
          <div>
            <h1>{config.title}</h1>
            <p>{config.subtitle}</p>
          </div>
        </div>
        <div className="ops-header-meta" aria-label="Workspace summary">
          <span>Live workspace</span>
          <span>{trucks.length} vehicles</span>
          <span>{workspace.auditEvents.length} audit events</span>
        </div>
      </div>
    );
  };

  const renderModuleTabs = () => (
    <nav className="ops-module-tabs" aria-label="FleetOps enterprise modules">
      {sectionLinks.map(item => (
        <Link
          key={item.section}
          to={item.path}
          className={`ops-module-tab ${item.section === section ? 'active' : ''}`}
        >
          {item.label}
        </Link>
      ))}
    </nav>
  );

  const renderFieldWork = () => (
    <>
      <div className="ops-metric-grid">
        {renderMetricCard('Inspections', workspace.inspections.length, <ClipboardCheck size={18} />)}
        {renderMetricCard('Open Issues', workspace.issueReports.filter(issue => issue.status !== 'resolved').length, <AlertTriangle size={18} />, 'warning')}
        {renderMetricCard('Offline Queue', workspace.offlineQueue.length, <WifiOff size={18} />, workspace.offlineQueue.length ? 'danger' : 'success')}
      </div>

      <div className="ops-grid two-col">
        <form className="ops-panel" onSubmit={handleInspectionSubmit}>
          <div className="ops-panel-header">
            <h2>DVIR Inspection</h2>
            <span className={`status-pill ${inspectionForm.offlineMode ? 'warning' : 'success'}`}>{inspectionForm.offlineMode ? 'Offline' : 'Live'}</span>
          </div>
          <label>Vehicle{renderTruckSelect(inspectionForm.truckId, value => setInspectionForm(form => ({ ...form, truckId: value, odometer: getTruck(value)?.mileage !== undefined ? Math.round(toDisplayDistance(getTruck(value)?.mileage, distanceUnit)).toString() : form.odometer })))}</label>
          <div className="ops-form-row">
            <label>Inspector<input value={inspectionForm.inspectorName} onChange={event => setInspectionForm(form => ({ ...form, inspectorName: event.target.value }))} required /></label>
            <label>Role<select value={inspectionForm.inspectorRole} onChange={event => setInspectionForm(form => ({ ...form, inspectorRole: event.target.value as FieldRole }))}><option>Driver</option><option>Mechanic</option></select></label>
            <label>Odometer ({distanceUnitLabel})<input type="text" inputMode="numeric" pattern="[0-9]*" value={inspectionForm.odometer} onChange={event => setInspectionForm(form => ({ ...form, odometer: event.target.value }))} required /></label>
          </div>
          <div className="ops-checklist">
            {checklistItems.map(item => (
              <label key={item.key} className="ops-check-item">
                <input type="checkbox" checked={inspectionForm.checklist[item.key]} onChange={event => setInspectionForm(form => ({ ...form, checklist: { ...form.checklist, [item.key]: event.target.checked } }))} />
                <span>{item.label}</span>
              </label>
            ))}
          </div>
          <label>Notes<textarea value={inspectionForm.notes} onChange={event => setInspectionForm(form => ({ ...form, notes: event.target.value }))} rows={3} /></label>
          <div className="ops-form-row">
            <label>Signature<input value={inspectionForm.signatureName} onChange={event => setInspectionForm(form => ({ ...form, signatureName: event.target.value }))} required /></label>
            <label>Photo<input type="file" accept="image/*" onChange={event => event.target.files?.[0] && readFileAsDataUrl(event.target.files[0], value => setInspectionForm(form => ({ ...form, photoDataUrl: value })))} /></label>
          </div>
          <label className="ops-toggle"><input type="checkbox" checked={inspectionForm.offlineMode} onChange={event => setInspectionForm(form => ({ ...form, offlineMode: event.target.checked }))} /> Save to offline queue</label>
          <button className="btn-primary" type="submit"><Plus size={16} /> Save Inspection</button>
        </form>

        <form className="ops-panel" onSubmit={handleIssueSubmit}>
          <div className="ops-panel-header"><h2>Issue Report</h2><span className="status-pill warning">Triage</span></div>
          <label>Vehicle{renderTruckSelect(issueForm.truckId, value => setIssueForm(form => ({ ...form, truckId: value })))}</label>
          <div className="ops-form-row">
            <label>Reported By<input value={issueForm.reportedBy} onChange={event => setIssueForm(form => ({ ...form, reportedBy: event.target.value }))} required /></label>
            <label>Severity<select value={issueForm.severity} onChange={event => setIssueForm(form => ({ ...form, severity: event.target.value as Severity }))}><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option></select></label>
            <label>Category<select value={issueForm.category} onChange={event => setIssueForm(form => ({ ...form, category: event.target.value as IssueFormState['category'] }))}><option>Safety</option><option>Mechanical</option><option>Tires</option><option>Brakes</option><option>Electrical</option><option>Body</option><option>Other</option></select></label>
          </div>
          <label>Description<textarea value={issueForm.description} onChange={event => setIssueForm(form => ({ ...form, description: event.target.value }))} rows={5} required /></label>
          <label>Photo<input type="file" accept="image/*" onChange={event => event.target.files?.[0] && readFileAsDataUrl(event.target.files[0], value => setIssueForm(form => ({ ...form, photoDataUrl: value })))} /></label>
          <button className="btn-primary" type="submit"><AlertTriangle size={16} /> Log Issue</button>
        </form>
      </div>

      <div className="ops-panel">
          <div className="ops-panel-header">
            <h2>Recent Field Activity</h2>
          <button
            className="btn-secondary small"
            type="button"
            onClick={() => {
              commitWorkspace(fleetOpsService.syncOfflineQueue(userId, actor));
              showMessage({ tone: 'success', title: 'Offline queue synced', detail: 'Queued field records are marked live.' });
            }}
            disabled={workspace.offlineQueue.length === 0}
          >
            <RefreshCw size={14} /> Sync Offline Queue
          </button>
        </div>
        <div className="ops-list">
          {workspace.inspections.length === 0 && renderEmptyState('No inspections yet', 'Saved DVIRs, photos, and signatures will appear here.', <ClipboardCheck size={22} />)}
          {workspace.inspections.slice(0, 6).map(inspection => (
            <div className="ops-list-item ops-record-item" key={inspection.id}>
              <button type="button" className="ops-record-summary" onClick={() => setDetailRecord({ type: 'inspection', record: inspection })}>
                <div>
                  <strong>{inspection.truckLabel}</strong>
                  <span>{inspection.inspectorRole} inspection by {inspection.inspectorName} · {formatDate(inspection.createdAt)}</span>
                </div>
                <span className={`status-pill ${statusClass(inspection.status)}`}>{inspection.status}</span>
              </button>
              <button className="btn-secondary small danger-text" type="button" onClick={() => deleteInspectionRecord(inspection)} title="Delete inspection record">
                <Trash2 size={14} /> Delete
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="ops-panel">
        <div className="ops-panel-header"><h2>Issue Triage Board</h2><span>{workspace.issueReports.length} reports</span></div>
        <div className="ops-grid cards">
          {workspace.issueReports.length === 0 && renderEmptyState('No open field issues', 'Driver and mechanic issue reports will land here for triage.', <AlertTriangle size={22} />)}
          {workspace.issueReports.slice(0, 8).map(issue => (
            <div className="ops-issue-card" key={issue.id}>
              <div className="ops-panel-header">
                <h2>{issue.category}</h2>
                <span className={`status-pill ${issue.severity}`}>{issue.severity}</span>
              </div>
              <p className="ops-muted">{issue.truckLabel} · {issue.reportedBy} · {formatDate(issue.createdAt)}</p>
              <p>{issue.description}</p>
              <div className="ops-card-kpis">
                <span>{issue.status}</span>
                {issue.photoDataUrl && <span>Photo attached</span>}
              </div>
              <div className="ops-actions">
                <button className="btn-secondary small" type="button" onClick={() => setDetailRecord({ type: 'issue', record: issue })}>View Details</button>
                {issue.status === 'open' && <button className="btn-secondary small" type="button" onClick={() => updateIssueStatus(issue.id, 'triaged')}>Triaged</button>}
                {!['converted-to-work-order', 'resolved'].includes(issue.status) && <button className="btn-primary small" type="button" onClick={() => convertIssueToWorkOrder(issue.id)}>Create Work Order</button>}
                {issue.status !== 'resolved' && <button className="btn-secondary small" type="button" onClick={() => updateIssueStatus(issue.id, 'resolved')}>Resolve</button>}
                <button className="btn-secondary small danger-text" type="button" onClick={() => deleteIssueRecord(issue)}><Trash2 size={14} /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );

  const renderWorkOrders = () => {
    const activeOrders = workspace.workOrders.filter(order => !['completed', 'invoiced'].includes(order.status));
    const invoiceValue = workspace.workOrders.reduce((sum, order) => sum + order.laborHours * order.laborRate + order.parts.reduce((partSum, part) => partSum + part.quantity * part.unitCost, 0), 0);

    return (
      <>
        <div className="ops-metric-grid">
          {renderMetricCard('Active Orders', activeOrders.length, <Wrench size={18} />, activeOrders.length ? 'warning' : 'success')}
          {renderMetricCard('Awaiting Approval', workspace.workOrders.filter(order => order.status === 'awaiting-approval').length, <ShieldCheck size={18} />)}
          {renderMetricCard('Invoice Value', formatCurrency(invoiceValue), <FileText size={18} />)}
        </div>

        <div className="ops-grid two-col">
          <form className="ops-panel" onSubmit={handleWorkOrderSubmit}>
            <div className="ops-panel-header"><h2>Create Work Order</h2><span className="status-pill">Shop</span></div>
            <label>Vehicle{renderTruckSelect(workOrderForm.truckId, value => setWorkOrderForm(form => ({ ...form, truckId: value })))}</label>
            <label>Title<input value={workOrderForm.title} onChange={event => setWorkOrderForm(form => ({ ...form, title: event.target.value }))} required /></label>
            <div className="ops-form-row">
              <label>Type<select value={workOrderForm.type} onChange={event => setWorkOrderForm(form => ({ ...form, type: event.target.value as WorkOrder['type'] }))}><option value="repair">Repair</option><option value="preventive">Preventive</option><option value="inspection">Inspection</option><option value="recall">Recall</option><option value="diagnostic">Diagnostic</option></select></label>
              <label>Priority<select value={workOrderForm.priority} onChange={event => setWorkOrderForm(form => ({ ...form, priority: event.target.value as Severity }))}><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option></select></label>
              <label>Scheduled<input type="date" value={workOrderForm.scheduledDate} onChange={event => setWorkOrderForm(form => ({ ...form, scheduledDate: event.target.value }))} /></label>
            </div>
            <div className="ops-form-row">
              <label>Assigned To<input value={workOrderForm.assignedTo} onChange={event => setWorkOrderForm(form => ({ ...form, assignedTo: event.target.value }))} /></label>
              <label>Hours<input type="text" inputMode="decimal" value={workOrderForm.laborHours} onChange={event => setWorkOrderForm(form => ({ ...form, laborHours: event.target.value }))} /></label>
              <label>Rate<input type="text" inputMode="decimal" value={workOrderForm.laborRate} onChange={event => setWorkOrderForm(form => ({ ...form, laborRate: event.target.value }))} /></label>
            </div>
            <div className="ops-form-row">
              <label>Reserve Part<select value={workOrderForm.partId} onChange={event => setWorkOrderForm(form => ({ ...form, partId: event.target.value }))}><option value="">None</option>{parts.map(part => <option key={part.id} value={part.id}>{part.name} {part.inventoryLevel !== undefined ? `(${part.inventoryLevel} in stock)` : ''}</option>)}</select></label>
              <label>Qty<input type="text" inputMode="numeric" pattern="[0-9]*" value={workOrderForm.partQuantity} onChange={event => setWorkOrderForm(form => ({ ...form, partQuantity: event.target.value }))} /></label>
            </div>
            {(() => {
              const selectedPart = parts.find(part => part.id === workOrderForm.partId);
              const quantity = Number(workOrderForm.partQuantity) || 0;
              return selectedPart?.inventoryLevel !== undefined && quantity > selectedPart.inventoryLevel
                ? <p className="ops-inline-alert danger">Only {selectedPart.inventoryLevel} {selectedPart.name} available. Reduce quantity or receive stock before reserving.</p>
                : null;
            })()}
            <label>Notes<textarea value={workOrderForm.notes} onChange={event => setWorkOrderForm(form => ({ ...form, notes: event.target.value }))} rows={3} /></label>
            <label className="ops-toggle"><input type="checkbox" checked={workOrderForm.approvalRequired} onChange={event => setWorkOrderForm(form => ({ ...form, approvalRequired: event.target.checked }))} /> Approval required</label>
            <button className="btn-primary" type="submit"><Plus size={16} /> Create Work Order</button>
          </form>

          <div className="ops-panel">
            <div className="ops-panel-header"><h2>Parts Reservations</h2><span className="status-pill">Inventory</span></div>
            <div className="ops-list">
              {workspace.workOrders.flatMap(order => order.parts).length === 0 && renderEmptyState('No parts reserved', 'Reserved parts from work orders will appear here with cost impact.', <PackageCheck size={22} />)}
              {workspace.workOrders.flatMap(order => order.parts.map(part => ({ order, part }))).slice(0, 8).map(({ order, part }) => (
                <div className="ops-list-item" key={`${order.id}-${part.partId}`}>
                  <div>
                    <strong>{part.partName}</strong>
                    <span>{part.quantity} reserved for {order.truckLabel}</span>
                  </div>
                  <span>{formatCurrency(part.quantity * part.unitCost)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="ops-grid cards">
          {workspace.workOrders.map(order => {
            const total = order.laborHours * order.laborRate + order.parts.reduce((sum, part) => sum + part.quantity * part.unitCost, 0);
            return (
              <div className="ops-panel ops-work-card" key={order.id}>
                <div className="ops-panel-header">
                  <h2>{order.title}</h2>
                  <span className={`status-pill ${statusClass(order.status)}`}>{order.status}</span>
                </div>
                <p className="ops-muted">{order.truckLabel} · {order.assignedTo} · {formatDate(order.scheduledDate)}</p>
                <div className="ops-card-kpis">
                  <span>{order.priority}</span>
                  <span>{order.laborHours}h</span>
                  <span>{formatCurrency(total)}</span>
                </div>
            <div className="ops-actions">
                  {order.status === 'awaiting-approval' && <button className="btn-secondary small" type="button" onClick={() => updateWorkOrderStatus(order, 'approved')}>Approve</button>}
                  {order.status === 'approved' && <button className="btn-secondary small" type="button" onClick={() => updateWorkOrderStatus(order, 'in-progress')}>Start</button>}
                  {order.status === 'in-progress' && <button className="btn-secondary small" type="button" onClick={() => updateWorkOrderStatus(order, 'completed')}>Complete</button>}
                  {order.status === 'completed' && <button className="btn-secondary small" type="button" onClick={() => updateWorkOrderStatus(order, 'invoiced')}>Invoice</button>}
                  {order.status === 'invoiced' && order.invoiceStatus !== 'paid' && <button className="btn-secondary small" type="button" onClick={() => {
                    commitWorkspace(fleetOpsService.updateWorkOrder(userId, order.id, { invoiceStatus: 'paid' }, actor));
                    showMessage({ tone: 'success', title: 'Invoice paid', detail: `${order.invoiceNumber || order.title} is marked paid.` });
                  }}>Mark Paid</button>}
                </div>
              </div>
            );
          })}
          {workspace.workOrders.length === 0 && renderEmptyState('No work orders yet', 'Create repair, PM, inspection, recall, or diagnostic work orders from here.', <Wrench size={22} />)}
        </div>
      </>
    );
  };

  const renderIntegrations = () => (
    <>
      <div className="ops-grid two-col">
        <div className="ops-panel">
          <div className="ops-panel-header">
            <h2>Closed-App Push</h2>
            <span className={`status-pill ${webPushStatus.subscribed ? 'success' : 'warning'}`}>{webPushStatus.subscribed ? 'Subscribed' : 'Setup'}</span>
          </div>
          <p className="ops-muted">{webPushStatus.message}</p>
          <label>VAPID Public Key<input value={pushVapidKey} onChange={event => setPushVapidKey(event.target.value)} placeholder="B..." /></label>
          <label>Subscription Endpoint<input value={pushEndpoint} onChange={event => setPushEndpoint(event.target.value)} placeholder="/api/push/subscribe or https://..." /></label>
          <div className="ops-actions">
            <button className="btn-primary" type="button" onClick={handlePushSubscribe}><Bell size={16} /> Enable Web Push</button>
            <button className="btn-secondary" type="button" onClick={async () => {
              const status = await notificationService.unsubscribeFromWebPush();
              setWebPushStatus(status);
              showMessage({ tone: 'info', title: 'Push disabled', detail: status.message });
            }}>Disable</button>
            <button className="btn-secondary" type="button" onClick={async () => {
              await notificationService.sendTestNotification();
              showMessage({ tone: 'success', title: 'Test notification sent', detail: 'Check your browser notification tray and FleetFix notification center.' });
            }}>Send Test</button>
          </div>
        </div>

        <div className="ops-panel">
          <div className="ops-panel-header"><h2>GPS/ELD CSV Import</h2><span className="status-pill success">Ready</span></div>
          <textarea value={csvImport} onChange={event => setCsvImport(event.target.value)} rows={7} />
          <button className="btn-primary" type="button" onClick={handleCsvImport}><Upload size={16} /> Import CSV</button>
        </div>
      </div>

      <div className="ops-grid cards">
        {workspace.connectors.map(connector => (
          <div className="ops-panel" key={connector.id}>
            <div className="ops-panel-header">
              <h2>{connector.name}</h2>
              <span className={`status-pill ${statusClass(connector.status)}`}>{connector.status}</span>
            </div>
            <p className="ops-muted">{connector.notes}</p>
            <div className="ops-card-kpis">
              <span>{connector.authMode}</span>
              <span>{connector.syncHealth}% health</span>
              <span>{connector.recordsSynced} records</span>
            </div>
            <div className="ops-actions">
              <button className="btn-secondary small" type="button" onClick={() => {
                commitWorkspace(fleetOpsService.updateConnector(userId, connector.id, { status: 'connected', syncHealth: 88 }, actor));
                showMessage({ tone: 'success', title: `${connector.id} connected`, detail: 'Connector credentials are saved for this company workspace.' });
              }}><KeyRound size={14} /> Connect</button>
              <button className="btn-primary small" type="button" onClick={() => runConnectorSync(connector.id)}>Run Sync</button>
            </div>
          </div>
        ))}
      </div>

      <div className="ops-panel">
        <div className="ops-panel-header"><h2>Telematics Events</h2><span>{workspace.telematicsEvents.length} records</span></div>
        <div className="data-table-wrapper">
          <table className="data-table">
            <thead><tr><th>Provider</th><th>Vehicle</th><th>Fault</th><th>Severity</th><th>Fuel</th><th>Date</th></tr></thead>
            <tbody>
              {workspace.telematicsEvents.length === 0 && (
                <tr><td colSpan={6}>{renderEmptyState('No telematics records', 'Connect a provider or import a GPS/ELD CSV to populate this table.', <RadioTower size={22} />)}</td></tr>
              )}
              {workspace.telematicsEvents.slice(0, 10).map(event => (
                <tr key={event.id}>
                  <td>{event.provider}</td>
                  <td>{event.truckLabel}</td>
                  <td>{event.faultCode || '—'}</td>
                  <td><span className={`status-pill ${event.severity}`}>{event.severity}</span></td>
                  <td>{event.fuelGallons ? `${event.fuelGallons} gal` : event.fuelPercent ? `${event.fuelPercent}%` : '—'}</td>
                  <td>{formatDate(event.recordedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  const renderCompliance = () => {
    const expired = workspace.complianceDocuments.filter(doc => doc.status === 'expired').length;
    const dueSoon = workspace.complianceDocuments.filter(doc => doc.status === 'due-soon').length;

    return (
      <>
        <div className="ops-metric-grid">
          {renderMetricCard('Valid Documents', workspace.complianceDocuments.filter(doc => doc.status === 'valid').length, <CheckCircle size={18} />, 'success')}
          {renderMetricCard('Due Soon', dueSoon, <AlertTriangle size={18} />, dueSoon ? 'warning' : 'success')}
          {renderMetricCard('Expired', expired, <ShieldCheck size={18} />, expired ? 'danger' : 'success')}
        </div>
        <div className="ops-grid two-col">
          <form className="ops-panel" onSubmit={handleComplianceSubmit}>
            <div className="ops-panel-header"><h2>Add Compliance Record</h2><span className="status-pill">Renewal</span></div>
            <label>Vehicle{renderTruckSelect(complianceForm.truckId, value => setComplianceForm(form => ({ ...form, truckId: value })))}</label>
            <div className="ops-form-row">
              <label>Type<select value={complianceForm.type} onChange={event => setComplianceForm(form => ({ ...form, type: event.target.value as ComplianceDocumentType }))}><option>DVIR</option><option>DOT Inspection</option><option>Insurance</option><option>Registration</option><option>IFTA</option><option>Permit</option><option>Driver File</option></select></label>
              <label>Number<input value={complianceForm.documentNumber} onChange={event => setComplianceForm(form => ({ ...form, documentNumber: event.target.value }))} required /></label>
              <label>Expires<input type="date" value={complianceForm.expirationDate} onChange={event => setComplianceForm(form => ({ ...form, expirationDate: event.target.value }))} required /></label>
            </div>
            <div className="ops-form-row">
              <label>Issuer<input value={complianceForm.issuer} onChange={event => setComplianceForm(form => ({ ...form, issuer: event.target.value }))} /></label>
              <label>Owner<input value={complianceForm.owner} onChange={event => setComplianceForm(form => ({ ...form, owner: event.target.value }))} /></label>
              <label>Attachment<input type="file" accept="image/*,.pdf" onChange={event => {
                const file = event.target.files?.[0];
                if (!file) return;
                if (file.type.startsWith('image/')) {
                  readFileAsDataUrl(file, value => setComplianceForm(form => ({ ...form, attachmentName: file.name, attachmentDataUrl: value })));
                } else {
                  setComplianceForm(form => ({ ...form, attachmentName: file.name, attachmentDataUrl: undefined }));
                }
              }} /></label>
            </div>
            <button className="btn-primary" type="submit"><Plus size={16} /> Save Record</button>
          </form>

          <div className="ops-panel">
            <div className="ops-panel-header"><h2>DVIR History</h2><span>{workspace.inspections.length} inspections</span></div>
            <div className="ops-list">
              {workspace.inspections.length === 0 && renderEmptyState('No DVIR history', 'Completed inspections will appear here for audit review.', <ClipboardCheck size={22} />)}
              {workspace.inspections.slice(0, 6).map(inspection => (
                <button type="button" className="ops-list-item ops-clickable" key={inspection.id} onClick={() => setDetailRecord({ type: 'inspection', record: inspection })}>
                  <div><strong>{inspection.truckLabel}</strong><span>{inspection.signatureName} · {formatDate(inspection.createdAt)}</span></div>
                  <span className={`status-pill ${statusClass(inspection.status)}`}>{inspection.status}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="ops-panel">
          <div className="ops-panel-header"><h2>Renewals & Documents</h2><span>{workspace.complianceDocuments.length} records</span></div>
          <div className="data-table-wrapper">
            <table className="data-table">
              <thead><tr><th>Type</th><th>Vehicle</th><th>Number</th><th>Issuer</th><th>Expires</th><th>Status</th></tr></thead>
              <tbody>
                {workspace.complianceDocuments.length === 0 && (
                  <tr><td colSpan={6}>{renderEmptyState('No compliance documents', 'Add insurance, registration, DOT, IFTA, permit, or driver-file records to start tracking renewals.', <ShieldCheck size={22} />)}</td></tr>
                )}
                {workspace.complianceDocuments.map(doc => (
                  <tr key={doc.id} className="ops-clickable-row" onClick={() => setDetailRecord({ type: 'compliance', record: doc })}><td>{doc.type}</td><td>{doc.truckLabel}</td><td>{doc.documentNumber}</td><td>{doc.issuer || '—'}</td><td>{formatDate(doc.expirationDate)}</td><td><span className={`status-pill ${doc.status}`}>{doc.status}</span></td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  };

  const renderPredictive = () => (
    <>
      <div className="ops-metric-grid">
        {renderMetricCard('Critical/High Risk', predictiveScores.filter(score => ['critical', 'high'].includes(score.riskLevel)).length, <AlertTriangle size={18} />, 'danger')}
        {renderMetricCard('Fault Codes', workspace.telematicsEvents.filter(event => event.faultCode).length, <Activity size={18} />, 'warning')}
        {renderMetricCard('Open Issues', workspace.issueReports.filter(issue => issue.status !== 'resolved').length, <Wrench size={18} />)}
      </div>
      <div className="ops-grid cards">
        {predictiveScores.map(score => (
          <div className="ops-panel ops-risk-card" key={score.truckId}>
            <div className="ops-panel-header">
              <h2>{score.truckLabel}</h2>
              <span className={`status-pill ${score.riskLevel}`}>{score.riskLevel}</span>
            </div>
            <div className="ops-risk-meter"><span style={{ width: `${score.riskScore}%` }} /></div>
            <div className="ops-card-kpis"><span>{score.riskScore}/100 risk</span><span>{score.nextServiceWindow}</span></div>
            <ul className="ops-compact-list">{score.drivers.map(driver => <li key={driver}>{driver}</li>)}</ul>
            <div className="ops-actions">
              {score.riskScore >= 60 && (
                <button className="btn-primary small" type="button" onClick={() => {
                  commitWorkspace(fleetOpsService.createWorkOrder(userId, {
                    truckId: score.truckId,
                    truckLabel: score.truckLabel,
                    title: `Predictive service: ${score.riskLevel} risk`,
                    type: 'diagnostic',
                    priority: score.riskLevel === 'critical' ? 'critical' : 'high',
                    status: 'awaiting-approval',
                    assignedTo: 'Internal shop',
                    requestedBy: actor,
                    scheduledDate: new Date(Date.now() + 2 * 86400000),
                    laborHours: 2,
                    laborRate: 95,
                    parts: [],
                    approvalRequired: true,
                    invoiceStatus: 'not-created',
                    notes: score.recommendedActions.join('; ')
                  }, actor));
                  showMessage({ tone: 'success', title: 'Predictive work order created', detail: `${score.truckLabel} now has a diagnostic work order.` });
                }}>Create Work Order</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );

  const renderCompany = () => {
    const pendingMembers = workspace.teamMembers.filter(member => isPendingMember(member.status));
    const activeMembers = workspace.teamMembers.filter(member => member.status === 'active');
    const disabledMembers = workspace.teamMembers.filter(member => member.status === 'disabled');
    const inviteServiceLabel = typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname)
      ? 'Production email delivery ready'
      : 'Production email API';

    return (
      <>
        <div className="ops-company-command">
          <div>
            <span className="ops-eyebrow">Enterprise Access Control</span>
            <h2>{companyName}</h2>
            <p>Invite by email, keep users pending until acceptance, then apply role-based access and preserve a full audit trail.</p>
          </div>
          <div className="ops-command-steps" aria-label="Team invite lifecycle">
            <span><Mail size={15} /> Email sent</span>
            <span><KeyRound size={15} /> Pending acceptance</span>
            <span><UserCheck size={15} /> Active user</span>
          </div>
        </div>

        <div className="ops-metric-grid">
          {renderMetricCard('Team Members', workspace.teamMembers.length, <Users size={18} />)}
          {renderMetricCard('Active Users', activeMembers.length, <CheckCircle size={18} />, 'success')}
          {renderMetricCard('Pending Invites', pendingMembers.length, <Mail size={18} />, pendingMembers.length ? 'warning' : 'success')}
          {renderMetricCard('Audit Events', workspace.auditEvents.length, <FileText size={18} />)}
        </div>

        <div className="ops-grid two-col enterprise-team-grid">
          <form className="ops-panel ops-invite-panel" onSubmit={handleMemberSubmit}>
            <div className="ops-panel-header">
              <div>
                <h2>Invite Team Member</h2>
                <p className="ops-muted">FleetFix emails the invite immediately and keeps access pending until the link is accepted.</p>
              </div>
              <span className="status-pill connected">{inviteServiceLabel}</span>
            </div>
            <label>Name<input value={memberForm.name} onChange={event => setMemberForm(form => ({ ...form, name: event.target.value }))} required disabled={Boolean(sendingInviteTarget)} /></label>
            <label>Email<input type="text" inputMode="email" value={memberForm.email} onChange={event => setMemberForm(form => ({ ...form, email: event.target.value }))} required disabled={Boolean(sendingInviteTarget)} /></label>
            <div className="ops-form-row">
              <label>Role<select value={memberForm.role} disabled={Boolean(sendingInviteTarget)} onChange={event => {
                const nextRole = event.target.value as TeamMember['role'];
                setMemberForm(form => ({ ...form, role: nextRole, permissions: permissionPresets[nextRole] }));
              }}><option value="admin">Admin</option><option value="editor">Editor</option><option value="viewer">Viewer</option><option value="driver">Driver</option><option value="mechanic">Mechanic</option><option value="dispatcher">Dispatcher</option></select></label>
              <label>Permissions<input value={memberForm.permissions} disabled={Boolean(sendingInviteTarget)} onChange={event => setMemberForm(form => ({ ...form, permissions: event.target.value }))} /></label>
            </div>
            <div className="ops-permission-preview">
              {memberForm.permissions.split(',').map(permission => permission.trim()).filter(Boolean).map(permission => <span key={permission}>{permission}</span>)}
            </div>
            <button className="btn-primary" type="submit" disabled={Boolean(sendingInviteTarget)}>
              {sendingInviteTarget ? <Loader2 size={16} className="spin-icon" /> : <Mail size={16} />}
              {sendingInviteTarget ? 'Sending Invite' : 'Send Email Invite'}
            </button>
          </form>

          <div className="ops-panel ops-access-panel">
            <div className="ops-panel-header"><h2>Access Posture</h2><span>{workspace.auditEvents.length} events</span></div>
            <div className="ops-access-stack">
              <div><strong>{pendingMembers.length}</strong><span>Pending acceptance</span></div>
              <div><strong>{activeMembers.length}</strong><span>Active workspace users</span></div>
              <div><strong>{disabledMembers.length}</strong><span>Disabled or revoked</span></div>
            </div>
            <div className="ops-list compact">
              {workspace.auditEvents.length === 0 && renderEmptyState('No audit history yet', 'FleetFix will record access, workflow, and compliance changes here.', <FileText size={22} />)}
              {workspace.auditEvents.slice(0, 6).map(event => (
                <div className="ops-list-item" key={event.id}>
                  <div><strong>{event.action}</strong><span>{event.actor} · {event.detail}</span></div>
                  <span>{formatDate(event.createdAt)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="ops-panel ops-directory-panel">
          <div className="ops-panel-header">
            <div>
              <h2>Team Directory</h2>
              <p className="ops-muted">Operational access, invite state, and expiration controls in one place.</p>
            </div>
            <span className="status-pill">{workspace.teamMembers.length} records</span>
          </div>
          {workspace.teamMembers.length === 0 ? (
            renderEmptyState('No team members invited', 'Invite drivers, mechanics, dispatchers, and managers with role-based access.', <Users size={22} />)
          ) : (
            <div className="ops-team-directory" role="table" aria-label="Team members">
              <div className="ops-directory-row header" role="row">
                <span>Member</span>
                <span>Role</span>
                <span>Status</span>
                <span>Invite</span>
                <span>Actions</span>
              </div>
              {workspace.teamMembers.map(member => {
                const pending = isPendingMember(member.status);
                const sending = sendingInviteTarget === member.id;
                return (
                  <div className={`ops-directory-row ${getTeamStatusClass(member.status)}`} role="row" key={member.id}>
                    <div className="ops-member-cell">
                      <strong>{member.name}</strong>
                      <span>{member.email}</span>
                    </div>
                    <div>
                      <strong>{member.role}</strong>
                      <span>{member.permissions.join(', ')}</span>
                    </div>
                    <div>
                      <span className={`status-pill ${getTeamStatusClass(member.status)}`}>{getTeamStatusLabel(member.status)}</span>
                      <small>{pending ? 'Awaiting link acceptance' : member.status === 'active' ? 'Can access workspace' : 'Access disabled'}</small>
                    </div>
                    <div className="ops-code-cell">
                      <strong>{member.inviteCode}</strong>
                      <span>{member.acceptedAt ? `Accepted ${formatDate(member.acceptedAt)}` : `Expires ${formatDate(member.inviteExpiresAt)}`}</span>
                    </div>
                    <div className="ops-actions compact">
                      {pending && <button className="btn-primary small" type="button" disabled={sending} onClick={() => sendTeamInviteEmail(member)}>{sending ? <Loader2 size={14} className="spin-icon" /> : <Mail size={14} />} {sending ? 'Sending' : 'Send'}</button>}
                      {pending && <button className="btn-secondary small" type="button" disabled={sending} onClick={() => resendInvite(member)}><RefreshCw size={14} /> Refresh</button>}
                      <button className="btn-secondary small" type="button" onClick={() => copyText(member.inviteLink, 'Invite link')}><Copy size={14} /> Link</button>
                      {member.status === 'active' && <button className="btn-secondary small" type="button" onClick={() => {
                        commitWorkspace(fleetOpsService.updateTeamMember(userId, member.id, { status: 'disabled' }, actor));
                        showMessage({ tone: 'success', title: 'Access updated', detail: `${member.name} is now disabled.` });
                      }}><UserX size={14} /> Disable</button>}
                      {member.status === 'disabled' && <button className="btn-secondary small" type="button" onClick={() => {
                        commitWorkspace(fleetOpsService.updateTeamMember(userId, member.id, { status: 'active', lastActive: new Date() }, actor));
                        showMessage({ tone: 'success', title: 'Access restored', detail: `${member.name} is active again.` });
                      }}><UserCheck size={14} /> Restore</button>}
                      {member.status !== 'disabled' && <button className="btn-secondary small danger-text" type="button" onClick={() => revokeAccess(member)}><UserX size={14} /> Revoke</button>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </>
    );
  };

  const renderReports = () => (
    <>
      <div className="ops-metric-grid">
        {renderMetricCard('Cost / Distance', formatCostPerDistance(reportMetrics.costPerMile, { unit: distanceUnit }), <BarChart3 size={18} />)}
        {renderMetricCard('Downtime Hours', reportMetrics.downtimeHours.toFixed(1), <Wrench size={18} />, reportMetrics.downtimeHours ? 'warning' : 'success')}
        {renderMetricCard('PM Compliance', `${reportMetrics.pmCompliance}%`, <ShieldCheck size={18} />, reportMetrics.pmCompliance >= 80 ? 'success' : 'warning')}
        {renderMetricCard('Utilization', `${reportMetrics.utilization}%`, <Activity size={18} />, 'success')}
      </div>
      <div className="ops-grid two-col">
        <div className="ops-panel">
          <div className="ops-panel-header"><h2>Maintenance Cost Trend</h2><button className="btn-secondary small" type="button" onClick={downloadReport}><Download size={14} /> CSV</button></div>
          <div className="ops-bar-chart">
            {reportMetrics.monthlyCosts.length === 0 && <p className="ops-muted">No cost history yet.</p>}
            {reportMetrics.monthlyCosts.map(item => (
              <div className="ops-bar-row" key={item.month}>
                <span>{item.month}</span>
                <div><span style={{ width: `${Math.min(100, item.cost / 35)}%` }} /></div>
                <strong>{formatCurrency(item.cost)}</strong>
              </div>
            ))}
          </div>
        </div>
        <div className="ops-panel">
          <div className="ops-panel-header"><h2>Fuel & Utilization</h2><span>{formatCurrency(reportMetrics.fuelCostEstimate)}</span></div>
          <div className="ops-report-grid">
            <div><strong>{reportMetrics.fuelGallons.toFixed(1)}</strong><span>Fuel gallons imported</span></div>
            <div><strong>{formatCurrency(reportMetrics.totalMaintenanceCost)}</strong><span>Total maintenance cost</span></div>
            <div><strong>{workspace.telematicsEvents.length}</strong><span>Telematics records</span></div>
            <div><strong>{workspace.workOrders.filter(order => order.status === 'completed' || order.status === 'invoiced').length}</strong><span>Completed orders</span></div>
            <div><strong>{reportMetrics.openWorkOrders}</strong><span>Open work orders</span></div>
            <div><strong>{reportMetrics.criticalFaults}</strong><span>High-risk fault codes</span></div>
          </div>
        </div>
      </div>
    </>
  );

  const renderDetailModal = () => {
    if (!detailRecord) return null;

    const { type, record } = detailRecord;
    const title = type === 'inspection'
      ? `${record.truckLabel} Inspection`
      : type === 'issue'
        ? `${record.category} Issue`
        : `${record.type} Compliance Record`;
    const imageSrc = 'photoDataUrl' in record
      ? record.photoDataUrl
      : 'attachmentDataUrl' in record
        ? record.attachmentDataUrl
        : undefined;

    return (
      <div className="modal-overlay ops-detail-overlay">
        <div className="modal-content ops-detail-modal">
          <div className="modal-header">
            <div>
              <h2>{title}</h2>
              <p>{'truckLabel' in record ? record.truckLabel : ''}</p>
            </div>
            <button type="button" className="close-btn" aria-label="Close detail" onClick={() => setDetailRecord(null)}>
              <X size={18} />
            </button>
          </div>
          <div className="ops-detail-body">
            {type === 'inspection' && (
              <>
                <div className="ops-detail-grid">
                  <span>Inspector</span><strong>{record.inspectorName}</strong>
                  <span>Role</span><strong>{record.inspectorRole}</strong>
                  <span>Odometer</span><strong>{formatDistance(record.odometer, { unit: distanceUnit })}</strong>
                  <span>Signature</span><strong>{record.signatureName}</strong>
                  <span>Status</span><strong>{record.status}</strong>
                  <span>Created</span><strong>{formatDate(record.createdAt)}</strong>
                </div>
                <div className="ops-checklist detail">
                  {Object.entries(record.checklist).map(([key, passed]) => (
                    <span key={key} className={`status-pill ${passed ? 'success' : 'danger'}`}>{key}: {passed ? 'passed' : 'failed'}</span>
                  ))}
                </div>
                <p className="ops-detail-notes">{record.notes || 'No notes recorded.'}</p>
              </>
            )}
            {type === 'issue' && (
              <>
                <div className="ops-detail-grid">
                  <span>Reported By</span><strong>{record.reportedBy}</strong>
                  <span>Severity</span><strong>{record.severity}</strong>
                  <span>Status</span><strong>{record.status}</strong>
                  <span>Created</span><strong>{formatDate(record.createdAt)}</strong>
                </div>
                <p className="ops-detail-notes">{record.description}</p>
              </>
            )}
            {type === 'compliance' && (
              <>
                <div className="ops-detail-grid">
                  <span>Document #</span><strong>{record.documentNumber}</strong>
                  <span>Issuer</span><strong>{record.issuer || 'Not set'}</strong>
                  <span>Owner</span><strong>{record.owner}</strong>
                  <span>Expires</span><strong>{formatDate(record.expirationDate)}</strong>
                  <span>Status</span><strong>{record.status}</strong>
                  <span>Attachment</span><strong>{record.attachmentName || 'No attachment'}</strong>
                </div>
              </>
            )}
            {imageSrc ? (
              <figure className="ops-detail-image">
                <img src={imageSrc} alt={`${title} attachment`} />
                <figcaption><ImageIcon size={14} /> Attached photo</figcaption>
              </figure>
            ) : (
              <div className="ops-empty-state compact"><ImageIcon size={20} /><strong>No image attached</strong></div>
            )}
            {(type === 'inspection' || type === 'issue') && (
              <div className="ops-actions ops-detail-actions">
                <button
                  className="btn-secondary small danger-text"
                  type="button"
                  onClick={() => {
                    if (type === 'inspection') {
                      deleteInspectionRecord(record);
                    } else {
                      deleteIssueRecord(record);
                    }
                  }}
                >
                  <Trash2 size={14} /> Delete Record
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSection = () => {
    switch (section) {
      case 'field-work':
        return renderFieldWork();
      case 'work-orders':
        return renderWorkOrders();
      case 'integrations':
        return renderIntegrations();
      case 'compliance':
        return renderCompliance();
      case 'predictive':
        return renderPredictive();
      case 'company':
        return renderCompany();
      case 'reports':
        return renderReports();
      default:
        return null;
    }
  };

  if (!currentUser) {
    return (
      <div className="page-container">
        <h1>{sectionConfig[section].title}</h1>
        <p>Please sign in to continue.</p>
      </div>
    );
  }

  return (
    <div className="page-container fleet-ops-page">
      {renderPageHeader()}
      {renderModuleTabs()}
      {operationMessage && (
        <div className={`ops-feedback ${operationMessage.tone}`} role="status">
          <div>
            <strong>{operationMessage.title}</strong>
            <span>{operationMessage.detail}</span>
          </div>
          <button type="button" onClick={() => setOperationMessage(null)} aria-label="Dismiss message">×</button>
        </div>
      )}
      {trucks.length === 0 ? (
        <div className="empty-state">Add a vehicle before using this module.</div>
      ) : (
        renderSection()
      )}
      {renderDetailModal()}
    </div>
  );
};

export default FleetOpsSuitePage;
